'use client';

import { websiteEnv } from '@/constants/website-env';
import { CLEAR_BACKEND_GRAPHQL_CACHE } from '@/typed-graphql/mutation';
import clsx from 'clsx';
import { stripIgnoredCharacters } from 'graphql';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

/**
 * @param {{
 *  tags?:string[];
 *  productIds?:number[];
 *  categoryIds?:number[];
 * }} props
 */
const ErrorFetchPage = props => {
  const { tags, productIds, categoryIds } = props;

  const isInitRef = useRef(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      if (isInitRef.current) {
        isInitRef.current = false;

        if (
          ((Array.isArray(productIds) && productIds.length !== 0) ||
            (Array.isArray(categoryIds) && categoryIds.length !== 0)) &&
          globalThis.fetch
        ) {
          await globalThis
            .fetch(`${process.env.NEXT_PUBLIC_BASE_PATH}/api/graphql`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Store: process.env.NEXT_PUBLIC_STORE_VIEW_CODE,
              },
              body: JSON.stringify({
                query: stripIgnoredCharacters(CLEAR_BACKEND_GRAPHQL_CACHE),
                variables: {
                  productIds: Array.isArray(productIds) ? productIds : [],
                  categoryIds: Array.isArray(categoryIds) ? categoryIds : [],
                },
              }),
            })
            .catch(() => {});
        }

        if (globalThis.fetch && Array.isArray(tags)) {
          await globalThis
            .fetch(`${process.env.NEXT_PUBLIC_BASE_PATH}/api/refresh-cache`, {
              method: 'POST',
              body: JSON.stringify({ tags }),
            })
            .catch(() => {});
        }
      }
    })();
  }, [tags, productIds, categoryIds]);

  return (
    <div
      className='px-2.5 pt-10 lg_px-0'
      data-page-error='graphql response error'
    >
      <div className='flex flex-col items-center justify-center'>
        <p className='text-[30px] font-semibold text-colordefault '>Something went wrong!</p>
        <p className='mt-2.5'>Please refresh the page and try again</p>
        <button
          type='button'
          disabled={loading}
          className={clsx(
            'flex h-10 items-center justify-center rounded-[3px] bg-80583F text-center text-white',
            'mt-10 px-5',
            'border-1 border-solid border-80583F',
            'lg_hover_bg-white lg_hover_text-80583F',
          )}
          onClick={async () => {
            setLoading(true);

            if (
              ((Array.isArray(productIds) && productIds.length !== 0) ||
                (Array.isArray(categoryIds) && categoryIds.length !== 0)) &&
              globalThis.fetch
            ) {
              await globalThis
                .fetch(`${process.env.NEXT_PUBLIC_BASE_PATH}/api/graphql`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Store: process.env.NEXT_PUBLIC_STORE_VIEW_CODE,
                  },
                  body: JSON.stringify({
                    query: stripIgnoredCharacters(CLEAR_BACKEND_GRAPHQL_CACHE),
                    variables: {
                      productIds: Array.isArray(productIds) ? productIds : [],
                      categoryIds: Array.isArray(categoryIds) ? categoryIds : [],
                    },
                  }),
                })
                .catch(() => {});
            }

            if (globalThis.fetch && Array.isArray(tags)) {
              await globalThis
                .fetch(`${process.env.NEXT_PUBLIC_BASE_PATH}/api/refresh-cache`, {
                  method: 'POST',
                  body: JSON.stringify({ tags }),
                })
                .catch(() => {});
            }

            setLoading(false);
            window.location.reload();
          }}
        >
          REFRESH THIS PAGE{loading && '...'}
        </button>
        <div className='mt-5'>
          <p className='mb-5 text-center text-[22px] text-colordefault'>Explore AW Bridal Brand...</p>
          <ul className='flex flex-col gap-5 lg_flex-row'>
            <li>
              <div>
                <Link
                  href={`/wedding-dresses`}
                  className='flex flex-col items-center underline'
                  prefetch={false}
                >
                  <img
                    src={`${process.env.NEXT_PUBLIC_BASE_PATH}/media/wysiwyg/pagenotfound1.jpg`}
                    alt='Wedding Dresses'
                  />
                  <span>Wedding Dresses</span>
                </Link>
              </div>
            </li>
            <li>
              <div>
                <Link
                  href={`/bridesmaid-dresses`}
                  className='flex flex-col items-center underline'
                  prefetch={false}
                >
                  <img
                    src={`${process.env.NEXT_PUBLIC_BASE_PATH}/media/wysiwyg/pagenotfound2.jpg`}
                    alt='Bridesmaid Dresses'
                  />
                  <span>Bridesmaid Dresses</span>
                </Link>
              </div>
            </li>
            <li>
              <div>
                <Link
                  href={`/mother-of-the-bride-dresses`}
                  className='flex flex-col items-center underline'
                  prefetch={false}
                >
                  <img
                    src={`${process.env.NEXT_PUBLIC_BASE_PATH}/media/wysiwyg/pagenotfound3.jpg`}
                    alt='Mother of the Bride Dresses'
                  />
                  <span>Mother of the Brides</span>
                </Link>
              </div>
            </li>
            {websiteEnv.isUnitedStates ? (
              <li>
                <div>
                  <Link
                    href={`/wedding-accessories`}
                    className='flex flex-col items-center underline'
                    prefetch={false}
                  >
                    <img
                      src={`${process.env.NEXT_PUBLIC_BASE_PATH}/media/wysiwyg/pagenotfound4.jpg`}
                      alt='Wedding Accessories'
                    />
                    <span>Accessories</span>
                  </Link>
                </div>
              </li>
            ) : null}
            <li>
              <div>
                <Link
                  href={`/${websiteEnv.isUnitedStates ? 'ready-to-ship' : 'wedding-guest-dresses'}`}
                  className='flex flex-col items-center underline'
                  prefetch={false}
                >
                  <img
                    src={`${process.env.NEXT_PUBLIC_BASE_PATH}/media/wysiwyg/pagenotfound6.jpg`}
                    alt={websiteEnv.isUnitedStates ? 'Ready to Ship' : 'Wedding Guest Dresses'}
                  />
                  <span>{websiteEnv.isUnitedStates ? 'Ready to Ship' : 'Wedding Guest Dresses'}</span>
                </Link>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ErrorFetchPage;
